import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { zhCN, enUS } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/contexts/LanguageContext";

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export function DatePicker({ value, onChange, className }: DatePickerProps) {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const locale = language === "en" ? enUS : zhCN;

  const date = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const validDate = date && isValid(date) ? date : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "h-9 px-2 text-xs font-normal justify-start gap-1.5 bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:text-white",
            !value && "text-slate-500",
            className
          )}
        >
          <CalendarIcon size={12} className="text-slate-500" />
          {validDate ? format(validDate, "yyyy-MM-dd") : (language === "en" ? "Pick date" : "选择日期")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#0d1117] border-white/10" align="start">
        <Calendar
          mode="single"
          selected={validDate}
          onSelect={(d) => {
            onChange(d ? format(d, "yyyy-MM-dd") : "");
            setOpen(false);
          }}
          locale={locale}
          className="text-white"
        />
      </PopoverContent>
    </Popover>
  );
}
